/**
 * The console's HTTP layer: the shared `ApiClient` (docs/CONTRACT.md §2/§4), wired to the persisted
 * session exactly as apps/web wires it. The client already understands both error shapes — the
 * envelope and §11's flat `{error:"not_approved", status}` — so a blocked call surfaces as
 * `ApiClientError.code === 'not_approved'` with `userStatus` set.
 */
import { ApiClient, ApiClientError, type AdminUserView, type UserView } from '@olmt/shared'
import { API_BASE_URL } from './env'
import { useAuthStore } from '../stores/authStore'

/** The single HTTP client for the whole console. */
export const api = new ApiClient({ baseUrl: API_BASE_URL })

// Keep the bearer token in sync with the persisted session. `persist` rehydrates synchronously
// from localStorage, so the first read already has the token when one was stored.
api.setToken(useAuthStore.getState().token)
useAuthStore.subscribe((state, previous) => {
  if (state.token !== previous.token) api.setToken(state.token)
})

/**
 * The console session's own user: `POST /api/auth/login` and `GET /api/me` answer a plain
 * `UserView` (§6) — an address only exists in the admin list.
 */
export type SessionUser = UserView

/**
 * §6's `AdminUserView` — `UserView` plus the account's email, admin-only on purpose. The console
 * renders `status`, `isAdmin` and `email` from this one row.
 */
export type AdminUser = AdminUserView

/** Human text for the failures a console screen can hit. */
export function describeError(caught: unknown, fallback: string): string {
  if (!(caught instanceof ApiClientError)) return fallback
  switch (caught.code) {
    case 'network':
      return 'Cannot reach the server. Check your connection.'
    case 'bad_credentials':
      /* The sign-in failure, and only that in this app: the same sentence apps/web shows for the
         code (`auth.errorBadCredentials`). */
      return 'Wrong email or password.'
    case 'email_taken':
      return 'That email already has an account.'
    case 'unauthorized':
      return 'This session is no longer valid. Sign in again.'
    case 'forbidden':
      return 'This account is not an administrator.'
    case 'not_approved':
      return caught.userStatus === 'pending'
        ? 'This account is waiting for approval.'
        : 'This account is not approved.'
    case 'rate_limited':
      return 'Too many attempts — wait a moment and try again.'
    case 'not_found':
      return 'That record no longer exists.'
    default:
      return fallback
  }
}
